import { useState } from 'react'
import { Button } from '@/components/ui/button'
import PaymentIntegrationModal from '@/components/PaymentIntegrationModal'

/**
 * Demo page for testing PaymentIntegrationModal
 * Route: /modal-demo
 */
export default function ModalDemo() {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [branchCount, setBranchCount] = useState(3)

  // Mock customer data with configurable number of branches
  const mockCustomer = {
    id: 'demo-customer-123',
    name: 'Demo Customer Ltd',
    companyName: 'Demo Customer Ltd',
    email: 'demo@example.com',
    phone: '01234 567890',
    branches: Array.from({ length: branchCount }, (_, i) => ({
      id: `branch-${i + 1}`,
      name: `Branch ${i + 1}`,
      address: `${i + 1} Demo Street, Demo City, DC${i + 1} 1AA`,
      customer_id: 'demo-customer-123',
      type_id: null,
      customer_type: null
    }))
  }

  const handleIntegrationComplete = (updatedCustomer) => {
    console.log('Integration completed:', updatedCustomer)
    setIsModalOpen(false)
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="bg-white rounded-lg border p-6">
          <h1 className="text-2xl font-bold mb-4">Payment Integration Modal Demo</h1>
          <p className="text-muted-foreground mb-6">
            Test the PaymentIntegrationModal with different numbers of branches to verify
            the scrolling behaviour and fixed header/footer layout.
          </p>

          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <label className="font-medium">Number of branches:</label>
              <input
                type="number"
                min="1"
                max="20"
                value={branchCount}
                onChange={(e) => setBranchCount(parseInt(e.target.value) || 1)}
                className="border rounded px-3 py-2 w-24"
              />
              <span className="text-sm text-muted-foreground">
                (Use 10+ to test scrolling)
              </span>
            </div>

            <Button onClick={() => setIsModalOpen(true)}>
              Open Payment Integration Modal
            </Button>
          </div>

          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-medium mb-2">Test Scenarios:</h3>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• <strong>3 branches</strong> - Should fit without scrolling</li>
              <li>• <strong>10 branches</strong> - Should require scrolling, footer stays visible</li>
              <li>• <strong>15 branches</strong> - Test heavy scrolling behaviour</li>
            </ul>
          </div>

          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="font-medium text-blue-900 mb-2">What to Check:</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>✓ Header stays fixed at top when scrolling</li>
              <li>✓ Footer buttons (Back/Confirm Setup) always visible at bottom</li>
              <li>✓ Content area scrolls independently</li>
              <li>✓ No buttons "falling off" the modal</li>
              <li>✓ Proper spacing and borders maintained</li>
            </ul>
          </div>
        </div>

        {/* Customer Data Preview */}
        <div className="bg-white rounded-lg border p-6">
          <h2 className="text-lg font-semibold mb-3">Mock Customer Data</h2>
          <pre className="bg-gray-50 p-4 rounded text-xs overflow-auto max-h-96">
            {JSON.stringify(mockCustomer, null, 2)}
          </pre>
        </div>
      </div>

      <PaymentIntegrationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        customer={mockCustomer}
        onIntegrationComplete={handleIntegrationComplete}
      />
    </div>
  )
}
